import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, Check, Copy, Lock, Share2, Bell, X } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import MetaTags from '@/components/MetaTags';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { trackCrawlView, trackBeginCheckout } from '@/utils/analytics';
import { resolveMediaUrl, isVideoUrl } from '@/utils/media';

const cityColors = {
  'Miami': '#FF6B4A',
  'Austin': '#FFB800',
  'New York': '#1E90FF',
  'Nashville': '#22C55E',
  'Chicago': '#00F5FF',
  'San Diego': '#A855F7',
  'default': '#1E90FF'
};

// Wavy underline SVG component — hand-drawn feel
const WavyUnderline = ({ color = '#FF2D78', width = 80 }) => (
  <svg width={width} height="8" viewBox={`0 0 ${width} 8`} fill="none" className="mt-3">
    <path
      d={`M0 4 C${width * 0.08} 1, ${width * 0.16} 7, ${width * 0.25} 4 S${width * 0.33} 1, ${width * 0.42} 4 S${width * 0.5} 7, ${width * 0.58} 4 S${width * 0.67} 1, ${width * 0.75} 4 S${width * 0.83} 7, ${width} 4`}
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

// Extra FAQs that display on every crawl
const additionalFaqs = [
  { question: 'What is included with my wristband?', answer: 'Your wristband gets you skip-the-line entry at every bar on the route, exclusive drink specials, and access to any reserved areas or party zones. Keep it on all night — it\'s your VIP pass to the crawl.' },
  { question: 'Do I need to print my ticket?', answer: 'Nope! Just pull up your confirmation email or digital ticket on your phone at check-in. We\'ll verify your name on the list and get you wristbanded and ready to go.' },
  { question: 'Can I upgrade from General to VIP on the day of the crawl?', answer: 'Yes! Subject to availability, you can upgrade to VIP at check-in for the price difference. We recommend upgrading online in advance to guarantee your VIP spot — day-of upgrades are first come, first served.' },
];

// Badge rotation per stop index for hand-placed feel
const badgeRotations = [-1, 1, -0.5, 0.8, -0.7];

const CrawlDetailPage = () => {
  const { id } = useParams();
  const [crawl, setCrawl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    fetchCrawl();
  }, [id]);

  // Expose ticket action for Navbar's GET TICKETS button
  useEffect(() => {
    if (!crawl) return;
    if (crawl.ticket_link) {
      window.__cbcTicketAction = () => window.open(crawl.ticket_link, '_blank', 'noopener,noreferrer');
    } else {
      window.__cbcTicketAction = () => setNotifyOpen(true);
    }
    return () => { window.__cbcTicketAction = null; };
  }, [crawl]);

  const fetchCrawl = async () => {
    try {
      const response = await axios.get(`${API}/crawls/${id}`);
      setCrawl(response.data);
      trackCrawlView(id, response.data.city, response.data.title);
    } catch (error) {
      console.error('Error fetching crawl:', error);
    }
    setLoading(false);
  };

  const handleBookNow = async () => {
    if (!crawl) return;

    // External ticket link — open in new tab
    if (crawl.ticket_link) {
      trackBeginCheckout(id, ticketType, 1, ticketType === 'vip' ? crawl.price_vip : crawl.price_general);
      window.open(crawl.ticket_link, '_blank', 'noopener,noreferrer');
      return;
    }

    // Stripe checkout — create session and open in new tab
    setBookingLoading(true);
    trackBeginCheckout(id, ticketType, 1, ticketType === 'vip' ? crawl.price_vip : crawl.price_general);
    try {
      const res = await axios.post(`${API}/checkout/create-session`, {
        crawl_id: id,
        ticket_type: ticketType,
        quantity: 1,
        customer_name: 'Guest',
        customer_email: 'guest@crawlbabycrawl.com',
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed. Try again.');
    }
    setBookingLoading(false);
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F0EE' }}>
        <div className="w-8 h-8 border-2 border-hot-pink border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!crawl) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24" style={{ backgroundColor: '#F5F0EE' }}>
        <div className="text-center">
          <h1 className="font-display text-[48px]" style={{ color: '#080010' }}>Crawl Not Found</h1>
          <Link to="/crawls">
            <button className="btn-primary px-8 py-4 mt-6">Browse All Crawls</button>
          </Link>
        </div>
      </div>
    );
  }

  const cityAccent = cityColors[crawl.city] || cityColors.default;
  const formattedDate = new Date(crawl.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const allFaqs = [...crawl.faqs, ...additionalFaqs];

  return (
    <div className="min-h-screen pt-20 pb-[72px] lg:pb-0" style={{
      backgroundColor: '#F5F0EE',
      backgroundImage: `
        linear-gradient(45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255,45,120,0.03) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255,45,120,0.03) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255,45,120,0.03) 75%)
      `,
      backgroundSize: '40px 40px',
      backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
    }} data-testid="crawl-detail-page">
      <MetaTags
        title={`${crawl.title} — ${crawl.city} Bar Crawl`}
        description={crawl.description || `Join the ${crawl.title} bar crawl in ${crawl.city}. ${crawl.bar_route?.length || 5} stops, one unforgettable night.`}
        image={crawl.image_url ? resolveMediaUrl(crawl.image_url) : undefined}
        path={`/crawls/${crawl.id}`}
      />

      {/* HERO */}
      <section className="relative h-[60vh] md:h-[70vh]" data-testid="crawl-detail-hero">
        <div className="absolute inset-0">
          {isVideoUrl(crawl.image_url) ? (
            <video
              src={resolveMediaUrl(crawl.image_url)}
              className="w-full h-full object-cover"
              muted autoPlay loop playsInline
            />
          ) : (
            <img
              src={resolveMediaUrl(crawl.image_url)}
              alt={crawl.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(to bottom, transparent 0%, rgba(255,45,120,0.15) 40%, rgba(255,45,120,0.25) 60%, rgba(245,240,238,0.7) 85%, #F5F0EE 100%)'
          }} />

          {/* CBC watermark — bottom right corner */}
          <div className="absolute bottom-4 right-6 z-10 pointer-events-none select-none" aria-hidden="true">
            <span className="font-display text-[14px] tracking-[3px] uppercase text-white/20">CBC</span>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-10 md:pb-14 z-10">
          <div className="max-w-[1400px] mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-[56px] md:text-[96px] lg:text-[120px] text-white uppercase leading-[0.9] tracking-wide drop-shadow-2xl"
            >
              {crawl.city}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-subheading text-[14px] md:text-[16px] text-[#00F5FF] uppercase tracking-[4px] mt-2"
            >
              {crawl.neighborhood}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-wrap items-center gap-3 mt-6"
            >
              <div className="flex items-center gap-2 bg-[#B8FF2E] px-4 py-2 rounded-full">
                <Calendar size={14} className="text-[#080010]" />
                <span className="font-body text-[#080010] text-sm font-semibold">{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2 bg-[#B8FF2E] px-4 py-2 rounded-full">
                <Clock size={14} className="text-[#080010]" />
                <span className="font-body text-[#080010] text-sm font-semibold">{crawl.time}</span>
              </div>
              <div className="flex items-center gap-2 bg-[#B8FF2E] px-4 py-2 rounded-full">
                <Users size={14} className="text-[#080010]" />
                <span className="font-body text-[#080010] text-sm font-semibold">{crawl.spots_remaining} spots left</span>
              </div>
              <div className="flex items-center gap-2 bg-[#B8FF2E] px-4 py-2 rounded-full">
                <MapPin size={14} className="text-[#080010]" />
                <span className="font-body text-[#080010] text-sm font-semibold">{crawl.bars_count} bars</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ticket perforation line — alternating pink/lime dashes */}
      <div className="w-full h-[3px]" style={{
        backgroundImage: 'repeating-linear-gradient(to right, #FF2D78 0px, #FF2D78 16px, transparent 16px, transparent 22px, #B8FF2E 22px, #B8FF2E 38px, transparent 38px, transparent 44px)',
        backgroundSize: '44px 3px'
      }} data-testid="perforation-line" />

      {/* MAIN CONTENT */}
      <section className="px-6 md:px-12 py-20" data-testid="crawl-detail-content">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-16">

          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-20">

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="border-l-[3px] border-[#FF2D78] pl-8"
            >
              <p className="font-body text-lg md:text-xl leading-[1.85] max-w-[620px]" style={{ color: '#080010' }}>
                {crawl.description}
              </p>
            </motion.div>

            {/* THE ROUTE */}
            <div data-testid="bar-route-section">
              <div className="mb-10">
                <h2 className="font-display text-[48px] md:text-[56px] uppercase leading-none" style={{ color: '#080010' }}>
                  The Route
                </h2>
                <WavyUnderline color="#FF2D78" width={100} />
              </div>

              <div className="relative">
                {/* Vertical connector line */}
                <div className="absolute left-[30px] md:left-[36px] top-[50px] bottom-[50px] w-[2px] hidden md:block" style={{
                  backgroundImage: 'repeating-linear-gradient(to bottom, #FF2D78 0px, #FF2D78 12px, transparent 12px, transparent 16px, #B8FF2E 16px, #B8FF2E 28px, transparent 28px, transparent 32px)',
                  backgroundSize: '2px 32px'
                }} />

                <div className="space-y-5">
                  {crawl.bar_route.map((bar, index) => {
                    const isFirst = index === 0;
                    const isLast = index === crawl.bar_route.length - 1;
                    const isOdd = index % 2 === 0;
                    const stopColor = isFirst ? '#B8FF2E' : isLast ? '#FF2D78' : (isOdd ? '#FF2D78' : '#B8FF2E');
                    const rotation = badgeRotations[index % badgeRotations.length];
                    const cardBg = index % 2 === 0 ? '#FFFFFF' : '#F7F5F3';

                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex gap-5 md:gap-7"
                        data-testid={`route-stop-${index}`}
                      >
                        {/* Stop number badge */}
                        <div className="flex-shrink-0 w-[60px] md:w-[72px] flex flex-col items-center pt-2">
                          <div className="flex flex-col items-center rounded-[14px] px-2 py-1.5 md:px-3 md:py-2" style={{ backgroundColor: '#080010' }}>
                            {isFirst && (
                              <span className="font-subheading text-[7px] md:text-[8px] uppercase tracking-[2px] font-bold text-white leading-none mb-0.5">
                                Check In
                              </span>
                            )}
                            {isLast && (
                              <span className="font-subheading text-[7px] md:text-[8px] uppercase tracking-[2px] font-bold leading-none mb-0.5" style={{ color: '#FF2D78' }}>
                                Finale
                              </span>
                            )}
                            <span className={`font-display leading-none font-bold ${isFirst || isLast ? 'text-[28px] md:text-[36px]' : 'text-[24px] md:text-[32px]'}`} style={{ color: stopColor }}>
                              {String(index + 1).padStart(2, '0')}
                            </span>
                            <span className="font-subheading text-[7px] md:text-[8px] uppercase tracking-[2px] text-white/70 leading-none mt-0.5">
                              {isFirst ? 'Start' : isLast ? 'End' : 'Stop'}
                            </span>
                          </div>
                        </div>

                        {/* Stop card */}
                        <div
                          className={`flex-1 rounded-lg overflow-hidden ${isFirst || isLast ? 'shadow-md' : 'shadow-sm'}`}
                          style={{
                            backgroundColor: cardBg,
                            borderLeft: `${isFirst || isLast ? '5' : '4'}px solid ${stopColor}`,
                            ...(isFirst ? { borderTop: '4px solid #B8FF2E', boxShadow: '0 4px 24px rgba(184,255,46,0.12)' } : {}),
                            ...(isLast ? { borderTop: '4px solid #FF2D78', boxShadow: '0 4px 24px rgba(255,45,120,0.12)' } : {}),
                          }}
                        >
                          <div className="p-5 md:p-6">
                            {/* CHECK IN / FINALE badge */}
                            {isFirst && (
                              <span className="inline-block font-mono text-[10px] uppercase tracking-[2px] px-3 py-1 rounded-full mb-2.5 font-bold" style={{ backgroundColor: '#B8FF2E', color: '#080010' }} data-testid="checkin-badge">
                                Check In
                              </span>
                            )}
                            {isLast && (
                              <span className="inline-block font-mono text-[10px] uppercase tracking-[2px] px-3 py-1 rounded-full mb-2.5 font-bold" style={{ backgroundColor: '#FF2D78', color: '#FFFFFF' }} data-testid="finale-badge">
                                Finale
                              </span>
                            )}

                            <h3 className={`font-display uppercase leading-tight ${isFirst || isLast ? 'text-[24px] md:text-[30px]' : 'text-[22px] md:text-[26px]'}`} style={{ color: '#080010' }}>
                              {bar.name}
                            </h3>

                            {/* Flavor text */}
                            {bar.flavor_text && (
                              <p className="font-body text-sm italic mt-1" style={{ color: '#080010' }}>
                                {bar.flavor_text}
                              </p>
                            )}

                            <p className="font-body text-sm mt-1.5" style={{ color: '#aaa' }}>{bar.address}</p>

                            {/* Solid bold info pills */}
                            <div className="flex flex-wrap items-center gap-2 mt-4">
                              {bar.special && (
                                <span className="inline-block font-mono text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider font-bold text-white" style={{ backgroundColor: '#FF2D78', transform: `rotate(${rotation}deg)` }}>
                                  {bar.special}
                                </span>
                              )}
                              {bar.venue_type && (
                                <span className="inline-block font-mono text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider font-bold" style={{ backgroundColor: '#B8FF2E', color: '#080010' }}>
                                  {bar.venue_type}
                                </span>
                              )}
                              {bar.vibe && (
                                <span className="inline-block font-mono text-[10px] px-3 py-1.5 rounded-full uppercase tracking-wider font-bold" style={{ backgroundColor: '#00F5FF', color: '#080010' }}>
                                  {bar.vibe}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* WHAT'S INCLUDED */}
            <div>
              <div style={{ height: '5px', backgroundColor: '#080010' }} />
              <div data-testid="perks-section" className="p-8 md:p-12" style={{ backgroundColor: '#B8FF2E' }}>
                <h2 className="font-display text-[48px] md:text-[56px] uppercase leading-none mb-8" style={{ color: '#080010' }}>
                  What's Included
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {crawl.perks.map((perk, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center gap-3"
                    >
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full" style={{ backgroundColor: 'rgba(8,0,16,0.15)' }}>
                        <Check size={14} style={{ color: '#080010' }} />
                      </div>
                      <span className="font-body font-semibold text-base" style={{ color: '#080010' }}>{perk}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              <div style={{ height: '5px', backgroundColor: '#080010' }} />
            </div>

            {/* FAQ — 6 total questions */}
            <div data-testid="faq-section">
              <div className="mb-8">
                <h2 className="font-display text-[48px] md:text-[56px] uppercase leading-none" style={{ color: '#080010' }}>
                  FAQ
                </h2>
                <WavyUnderline color="#FF2D78" width={70} />
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {allFaqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="bg-white rounded-[12px] px-6"
                    style={{ border: '1px solid #FFB3C6', borderBottom: '1px solid #FFB3C6' }}
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left font-subheading text-base py-5 hover:no-underline" style={{ color: '#080010' }}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-body pb-5" style={{ color: 'rgba(8,0,16,0.6)' }}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* RIGHT COLUMN — Ticket Widget (desktop only, mobile uses fixed bottom bar) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-xl overflow-hidden shadow-sm" style={{ border: '1px solid #E5E5E5' }} data-testid="ticket-section">
              <div style={{ height: '4px', backgroundColor: '#FF2D78' }} />

              <div className="p-7 space-y-6">
                <div>
                  <h3 className="font-display text-[36px] uppercase leading-none" style={{ color: '#FF2D78' }}>Get Tickets</h3>
                  <p className="font-mono text-[11px] mt-2 uppercase tracking-[2px]" style={{ color: '#B8FF2E' }}>{crawl.spots_remaining} spots remaining</p>
                </div>

                {/* Single CTA */}
                {crawl.ticket_link ? (
                  <a
                    href={crawl.ticket_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-now-btn"
                    className="w-full block text-center font-display text-[20px] tracking-[1px] uppercase bg-hot-pink text-white py-5 rounded-lg transition-all duration-300 hover:bg-[#E0005A] hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(255,45,120,0.4)]"
                  >
                    GET TICKETS
                  </a>
                ) : (
                  <button
                    onClick={() => setNotifyOpen(true)}
                    data-testid="book-now-btn"
                    className="w-full font-display text-[20px] tracking-[1px] uppercase bg-hot-pink text-white py-5 rounded-lg transition-all duration-300 hover:bg-[#E0005A] hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(255,45,120,0.4)] flex items-center justify-center gap-2"
                  >
                    <Bell size={18} /> GET TICKETS
                  </button>
                )}

                {/* Eventbrite note */}
                <p className="font-mono text-[10px] text-center uppercase tracking-[2px]" style={{ color: '#999' }}>
                  Tickets sold on Eventbrite
                </p>

                {/* Secure Checkout */}
                <div className="flex items-center justify-center gap-2" style={{ color: '#999' }}>
                  <Lock size={12} />
                  <span className="font-mono text-[10px] uppercase tracking-[2px]">Secure Checkout</span>
                </div>

                {/* Share link */}
                <button
                  onClick={copyShareLink}
                  data-testid="share-link-btn"
                  className="w-full flex items-center justify-center gap-2 hover:opacity-80 transition-colors font-body text-sm"
                  style={{ color: '#00F5FF' }}
                >
                  <Share2 size={14} />
                  Copy Link &middot; Bring Your Crew
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notify Me Modal */}
      {notifyOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="notify-modal">
          <div className="relative bg-[#0F0E1A] rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF2D78] via-[#1E90FF] to-[#00F5FF] rounded-t-2xl" />
            <button
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              onClick={() => setNotifyOpen(false)}
              data-testid="notify-modal-close"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h2 className="font-display text-[32px] text-white uppercase leading-none mt-4">
                Get Notified
              </h2>
              <p className="font-body text-white/60 text-sm mt-3 mb-6">
                Drop your number and we'll text you the second tickets go live for this crawl.
              </p>
              <div className="overflow-hidden" style={{ maxHeight: '120px' }}>
                <iframe
                  src="https://laylo.com/laylo-sacqnuz/i4hnd4/embed?theme=dark&background=transparent&customColor=FFFFFF&minimal=true"
                  className="w-full border-0"
                  style={{ height: '200px', marginTop: '-10px' }}
                  loading="lazy"
                  title="Notify Me"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Ticket Bar — fixed bottom on small screens */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#080010] border-t-[3px] border-[#FF2D78] px-4 py-3 flex items-center justify-between lg:hidden" data-testid="mobile-ticket-bar">
        <div>
          <p className="font-display text-white text-[18px] uppercase leading-none">{crawl.city}</p>
          <p className="font-mono text-[10px] text-[#B8FF2E] uppercase tracking-[1px] mt-0.5">{crawl.spots_remaining} spots left</p>
        </div>
        {crawl.ticket_link ? (
          <a
            href={crawl.ticket_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-book-now-btn"
            className="font-display text-[15px] tracking-[1px] uppercase bg-hot-pink text-white px-6 py-3 rounded-lg min-h-[48px] flex items-center"
          >
            GET TICKETS
          </a>
        ) : (
          <button
            onClick={() => setNotifyOpen(true)}
            data-testid="mobile-book-now-btn"
            className="font-display text-[15px] tracking-[1px] uppercase bg-hot-pink text-white px-6 py-3 rounded-lg min-h-[48px] flex items-center gap-2"
          >
            <Bell size={16} /> GET TICKETS
          </button>
        )}
      </div>
    </div>
  );
};

export default CrawlDetailPage;
